import { useEffect, useMemo, useState } from 'react';
// Import the reference diagrams as inline React components (SVGR) so they render
// directly into the DOM — no URL resolution that can fail and show a broken image.
import { ReactComponent as SequentialApproval } from '../assets/bpm-design/sequential-approval.svg';
import { ReactComponent as ParallelApproval } from '../assets/bpm-design/parallel-approval.svg';
import { ReactComponent as FusionImplementation } from '../assets/bpm-design/fusion-implementation.svg';

const GUIDE_IMAGES = [
  {
    id: 'sequential',
    Component: SequentialApproval,
    title: 'Sequential routing',
    caption: 'Approvers act one after another (typical line manager → HR path).',
  },
  {
    id: 'parallel',
    Component: ParallelApproval,
    title: 'Parallel routing',
    caption: 'Multiple approvers at the same stage must all complete before merge.',
  },
  {
    id: 'fusion',
    Component: FusionImplementation,
    title: 'Fusion Dev setup',
    caption: 'Where to implement the structured design in your Dev pod.',
  },
];

function normalizeApiImages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, i) => {
      if (!item) return null;
      if (typeof item === 'string') {
        const src = item.trim();
        if (!src || src === '#' || src.startsWith('data:,')) return null;
        return { id: `api-${i}`, src, title: `Diagram ${i + 1}`, caption: '' };
      }
      const src = item.src || item.url || item.href;
      if (!src || String(src).trim() === '' || String(src) === '#') return null;
      return {
        id: item.id || `api-${i}`,
        src: String(src),
        fallbackSrc: item.fallbackSrc,
        title: item.title || item.label || `Diagram ${i + 1}`,
        caption: item.caption || item.description || '',
      };
    })
    .filter(Boolean);
}

function DiagramImage({ src, fallbackSrc, alt, className }) {
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (fallbackSrc && currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc);
        }
      }}
    />
  );
}

function approverLabel(approver) {
  if (!approver || typeof approver !== 'object') return 'Approver';
  const kind = String(approver.kind || 'approver').replace(/([A-Z])/g, ' $1').trim();
  const ref = approver.ref && approver.ref !== 'n/a' ? ` · ${approver.ref}` : '';
  return `${kind}${ref}`;
}

function GeneratedFlowDiagram({ design }) {
  const stages = useMemo(() => {
    const raw = design?.stages;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .map((stage, idx) => ({
        order: stage.order ?? idx + 1,
        routing: stage.routing || 'sequential',
        approvers: Array.isArray(stage.approvers) ? stage.approvers : [],
      }));
  }, [design]);

  if (stages.length === 0) return null;

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900">
          Generated approval flow
        </p>
        {design?.transactionHint && (
          <p className="text-[11px] text-emerald-800/90 mt-0.5">{design.transactionHint}</p>
        )}
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-max items-stretch gap-2">
          <div className="flex shrink-0 items-center rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] font-medium text-slate-700">
            Policy
          </div>
          {stages.map((stage, i) => (
            <div key={stage.order ?? i} className="flex items-center gap-2">
              <span className="text-slate-400 text-xs" aria-hidden>
                →
              </span>
              <div
                className={`rounded-md border px-3 py-2 min-w-[120px] max-w-[200px] ${
                  stage.routing === 'parallel'
                    ? 'border-violet-300 bg-violet-50'
                    : 'border-cyan-300 bg-cyan-50'
                }`}
              >
                <p className="text-[10px] font-semibold uppercase text-slate-500">
                  Stage {stage.order}
                  <span className="ml-1 font-normal normal-case text-slate-400">
                    ({stage.routing})
                  </span>
                </p>
                <ul className="mt-1 space-y-0.5 text-[11px] text-slate-800">
                  {stage.approvers.length === 0 ? (
                    <li className="text-slate-500">No approvers listed</li>
                  ) : (
                    stage.approvers.map((a, j) => (
                      <li key={j} className="truncate" title={approverLabel(a)}>
                        {approverLabel(a)}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          ))}
          <span className="text-slate-400 text-xs self-center" aria-hidden>
            →
          </span>
          <div className="flex shrink-0 items-center rounded-md border border-emerald-300 bg-emerald-100/80 px-3 py-2 text-[11px] font-medium text-emerald-900">
            Complete
          </div>
        </div>
      </div>
    </div>
  );
}

function DiagramGallery({ images }) {
  if (!images.length) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {images.map((img) => (
        <figure
          key={img.id || img.src}
          className="rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden"
        >
          {img.Component ? (
            <img.Component
              role="img"
              aria-label={img.title}
              className="w-full h-auto block bg-white min-h-[72px]"
            />
          ) : (
            <DiagramImage
              src={img.src}
              fallbackSrc={img.fallbackSrc}
              alt={img.title}
              className="w-full h-auto block bg-white min-h-[72px]"
            />
          )}
          <figcaption className="px-2.5 py-2 border-t border-slate-100">
            <p className="text-[11px] font-medium text-slate-700">{img.title}</p>
            {img.caption ? (
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{img.caption}</p>
            ) : null}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

/**
 * Reference diagrams and generated flow under "Generate structured design".
 */
export default function BpmStructuredDesignVisuals({ design, apiImages, hideGeneratedFlow = false }) {
  const galleryImages = useMemo(() => {
    const fromApi = normalizeApiImages(apiImages);
    if (fromApi.length > 0) return fromApi;
    return GUIDE_IMAGES;
  }, [apiImages]);

  return (
    <div className="space-y-3 mt-2 pt-3 border-t border-cyan-100">
      <div>
        <p className="text-xs font-semibold text-cyan-900">Reference diagrams</p>
      </div>
      <DiagramGallery images={galleryImages} />
      {design && !hideGeneratedFlow && <GeneratedFlowDiagram design={design} />}
    </div>
  );
}
